import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../services/authService';
import './Layout.css';

const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordMessage, setChangePasswordMessage] = useState('');

  const handleLogout = async () => {
    setShowChangePassword(false);
    await logout();
    navigate('/login');
  };

  const resetChangePasswordState = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setChangePasswordError('');
    setChangePasswordMessage('');
    setChangePasswordLoading(false);
  };

  const openChangePassword = () => {
    resetChangePasswordState();
    setShowChangePassword(true);
  };

  const closeChangePassword = () => {
    setShowChangePassword(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError('');
    setChangePasswordMessage('');

    if (newPassword !== confirmPassword) {
      setChangePasswordError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 8) {
      setChangePasswordError('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setChangePasswordError('비밀번호는 최소 하나의 특수문자를 포함해야 합니다.');
      return;
    }

    setChangePasswordLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setChangePasswordMessage('비밀번호가 변경되었습니다. 다시 로그인해주세요.');
    } catch (err) {
      setChangePasswordError(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const homePath = !isAuthenticated
    ? '/login'
    : user?.is_admin || user?.is_staff
    ? '/admin/members'
    : user?.status === 'submitted'
    ? '/completed'
    : '/consent';

  return (
    <div className="layout">
      <header className="header">
        <div className="header-content">
          <Link to={homePath} className="logo-link">
            <img 
              src="/images/로고1.png" 
              alt="BnH 세무법인" 
              className="logo-top"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling?.remove();
                const fallback = document.createElement('span');
                fallback.textContent = 'BnH 세무법인';
                fallback.className = 'logo-fallback';
                e.target.parentNode.appendChild(fallback);
              }}
            />
          </Link>
          {isAuthenticated && (
            <nav className="nav">
              {user?.is_admin || user?.is_staff ? (
                <>
                  <Link to="/admin/members">회원 관리</Link>
                </>
              ) : (
                <>
                  <Link to="/consent">동의서</Link>
                  <Link to="/basic-info">기본정보</Link>
                  <Link to="/survey">설문지</Link>
                </>
              )}
              <button onClick={openChangePassword} className="password-change-btn">
                비밀번호 변경
              </button>
              <button onClick={handleLogout} className="logout-btn">
                로그아웃
              </button>
            </nav>
          )}
        </div>
      </header>
      <main className="main-content">{children}</main>
      <footer className="footer">
        <img 
          src="/images/로고2.png" 
          alt="BnH 세무법인" 
          className="logo-bottom"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </footer>
      {showChangePassword && (
        <div className="modal-overlay" role="presentation">
          <div className="modal" role="dialog" aria-modal="true" aria-label="비밀번호 변경">
            <div className="modal-header">
              <h2>비밀번호 변경</h2>
            </div>
            <form onSubmit={handleChangePassword} className="modal-form">
              <div className="form-group">
                <label htmlFor="currentPassword">현재 비밀번호</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">새 비밀번호</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">새 비밀번호 확인</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {changePasswordError && <div className="error-message">{changePasswordError}</div>}
              {changePasswordMessage && <div className="success-message">{changePasswordMessage}</div>}
              <div className="modal-actions">
                {changePasswordMessage ? (
                  <button type="button" className="submit-btn" onClick={handleLogout}>
                    다시 로그인
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={closeChangePassword}
                      disabled={changePasswordLoading}
                    >
                      취소
                    </button>
                    <button type="submit" className="submit-btn" disabled={changePasswordLoading}>
                      {changePasswordLoading ? '처리 중...' : '변경'}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;

